 
import { faChartBar } from '@fortawesome/free-solid-svg-icons';
import { faChartLineUp, faChartMixedUpCircleDollar, faGrid2, faHouse, faShop } from '@fortawesome/pro-solid-svg-icons';
import { ProFormaModelIds } from 'constants/proForma';
import { IOptionAnyValue } from 'bricks/types';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { GlobalKeys } from 'helpers/localStorage';
import { IProjectionType, ProjectionType } from './types';
import { SensitivityTableValues } from './components/SensitivityTable/types';

export const PRO_FORMA_TABLE_NAV = {
    PRO_FORMA: { name: 'Pro Forma', icon: faChartLineUp },
    RETURNS_AND_WATERFALL: { name: 'Returns & Waterfall Analysis', icon: faChartBar },
    SENSITIVITY: { name: 'Sensitivity', icon: faChartMixedUpCircleDollar },
    EXECUTIVE_SUMMARY: { name: 'Executive Summary', icon: faGrid2 },
};
export const PRO_FORMA_TABLE_NAV_NAMES = Object.values(PRO_FORMA_TABLE_NAV).map(nav => nav.name);

export const PRO_FORMA_TABS = {
    GENERAL_INFORMATION: { prefix: 'A', name: 'General Information' },
    DEVELOPMENT: { prefix: 'B', name: 'Development' },
    OPERATIONS: { prefix: 'C', name: 'Operations' },
    DISPOSITION: { prefix: 'D', name: 'Disposition' },
};
export const PRO_FORMA_TAB_NAMES = Object.values(PRO_FORMA_TABS).map(tab => tab.name);

export const RETURNS_AND_WATERFALL_TABS = {
    RETURNS: 'Returns',
    WATERFALL_ANALYSIS: 'Waterfall Analysis',
};
export const RETURNS_AND_WATERFALL_TAB_NAMES = Object.values(RETURNS_AND_WATERFALL_TABS);

export const PROJECT_ACTUALS_ICON_WITH_TOOLTIP_TEXT = 'This field has been updated based on project data.';
export const RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_GENERAL_INFO_TEXT = 'Rentable SF exceeds built gross SF. Update rentable SF from Operations.';
export const RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT = 'Rentable SF exceeds built gross SF. Update built gross SF from General information.';
export const DATA_CENTER_INFO_ICON_TEXT = 'Industry standard is 25-35 SF of white space per rack';
export const DISABLED_ESTIMATED_UNITS_TOOLTIP = 'Estimated no. of units is derived from the income table in Operations.';
export const DISABLED_EFFICIENCY_RATIO_TOOLTIP = 'Efficiency ratio is calculated using RSF from the income table in Operations.';
export const LOADING_TEXT = 'Loading...';

export const FORM_ROW_CLASS_NAME = 'd-flex mb-2';
export const FORM_LABEL_CLASS_NAME = 'pl-0 col-4 my-auto font-weight-normal text-dark text-nowrap';
export const CALCULATE_SIZE_HEADER_CLASS_NAME = 'p-0 pb-1 text-dark font-weight-normal';

export const HOLD_PERIOD_TOOLTIP_TEXT = 'Used to calculate sale month beginning from start month.';

export const ESTIMATED_RACKS_TOOLTIP_TEXT = 'Estimated no. of racks is derived from the income table in Operations.';

export const GENERAL_INFORMATION_EXPANDABLE_SECTIONS = [
    {
        number: 1,
        title: 'Basic Details',
        subtitle: 'Information about this investment\'s name and location',
    },
    {
        number: 2,
        title: 'Key Dates',
        subtitle: 'Determine key dates and durations for this pro forma',
    },
    {
        number: 3,
        title: 'Size',
        subtitle: 'Details about this investment\'s property size and building',
    },
];

export const DEVELOPMENT_EXPANDABLE_SECTIONS = [
    {
        number: 1,
        title: 'Development Timeline',
        subtitle: 'Determine development timeline for this investment',
    },
    {
        number: 2,
        title: 'Uses',
        subtitle: 'Information about the investment\'s allocation of funds',
    },
    {
        number: 3,
        title: 'Sources',
        subtitle: 'Information about the investment\'s sources of funds',
    },
];

export const OPERATIONS_EXPANDABLE_SECTIONS = [
    {
        number: 1,
        title: 'Operating Information',
        subtitle: 'Information about this investment\'s incoming revenue',
    },
    {
        number: 2,
        title: 'Income',
        subtitle: 'Overview of this investment\'s total income',
    },
    {
        number: 3,
        title: 'Expenses',
        subtitle: 'Overview of this investment\'s total expenses',
    },
    {
        number: 4,
        title: 'Permanent Debt',
        subtitle: 'Include permanent debt in this Pro Forma',
    },
];

export const DISPOSITION_EXPANDABLE_SECTIONS = [
    {
        number: 1,
        title: 'Assumptions',
        subtitle: 'Assumptions about the sale of this investment',
    },
    {
        number: 2,
        title: 'Forecast',
        subtitle: 'Estimate projections for this project on a trended and untrended basis',
    },
    {
        number: 3,
        title: 'Sale Proceeds',
        subtitle: 'Review the overview of proceeds at sale',
    },
    {
        number: 4,
        title: 'Valuation',
        subtitle: 'Review the forecasted valuation of this property',
    },
];

export enum BOE_DISCREPANCY_METRIC_NAMES {
    EFFICIENCY_RATIO = 'Efficiency ratio',
    TOTAL_DEVELOPMENT_COSTS = 'Total development costs',
    TOTAL_EQUITY = 'Total equity',
    TOTAL_CONSTRUCTION_DEBT = 'Total construction debt',
    TOTAL_UNITS = 'Total units',
    TOTAL_SUITES = 'Total suites',
    TOTAL_FLOORS = 'Total floors',
    TOTAL_TENANTS = 'Total tenants',
    GROSS_RENT = 'Gross rent',
    TOTAL_RENTAL_INCOME = 'Total rental income',
    TOTAL_EXPENSES = 'Total expenses',
    NET_OPERATING_INCOME = 'Net operating income',
    STABILIZED_VALUE = 'Stabilized value',
}

export const STRUCTURED_PARKING = 'structured';
export const SURFACE_PARKING = 'surface';
export const UNDERGROUND_PARKING = 'underground';
export const TRAILER_PARKING = 'trailer';
export const NO_PARKING = 'none';

export const noParkingOption = { label: 'No parking', value: NO_PARKING };

export const parkingOptions = [
    { label: 'Structured', value: STRUCTURED_PARKING },
    { label: 'Surface', value: SURFACE_PARKING },
    { label: 'Underground', value: UNDERGROUND_PARKING },
    noParkingOption,
];

export const industrialParkingOptions = [
    ...parkingOptions,
    { label: 'Trailer', value: TRAILER_PARKING },
];

export const proFormaModelParkingOptions: Record<ProFormaModelIds, IOptionAnyValue[]> = {
    [ProFormaModelIds.APARTMENT]: parkingOptions,
    [ProFormaModelIds.DATA_CENTER]: parkingOptions,
    [ProFormaModelIds.INDUSTRIAL]: industrialParkingOptions,
    [ProFormaModelIds.RETAIL]: parkingOptions,
    [ProFormaModelIds.OFFICE]: parkingOptions,
    [ProFormaModelIds.APARTMENT_WITH_RETAIL]: parkingOptions,
    [ProFormaModelIds.STUDENT_HOUSING]: parkingOptions,
    [ProFormaModelIds.LAND_DEVELOPMENT]: parkingOptions,
    [ProFormaModelIds.RESIDENTIAL_CONDO]: parkingOptions,
    [ProFormaModelIds.HOTEL_HOSPITALITY]: parkingOptions,
    [ProFormaModelIds.SELF_STORAGE]: parkingOptions,
};

export const proFormaModelsWithFarToggle = new Set([
    ProFormaModelIds.APARTMENT,
    ProFormaModelIds.APARTMENT_WITH_RETAIL,
    ProFormaModelIds.OFFICE,
]);

export const POPULATE_USES_METHOD = {
    IMPORT_FROM_PREVIOUS_PROJECT: {
        title: 'Import from a previous deal or project',
        subtitle: 'Start with budget categorizations, estimates, and curve fits used previously.',
    },
    START_WITH_BACK_OF_ENVELOPE: {
        title: 'Start with my Back-Of-Envelope estimates',
        subtitle: 'Start with high level assumptions established using Back-Of-Envelope.',
    },
    USE_RECOMMENDED_CATEGORIES: {
        title: 'Use recommended categories',
        subtitle: 'Start with our recommended budget line types for this project type.',
    },
    START_FROM_SCRATCH: {
        title: 'Start from scratch',
    },
};

export const PROJECTION_TYPE: { [key: string]: ProjectionType } = {
    LINEAR: 'linear',
    NORMAL: 'normal',
    GAMMA: 'gamma',
};

export const PROJECTION_TYPES: IProjectionType[] = [
    {
        projectionType: PROJECTION_TYPE.LINEAR,
        projectionVariable: 1,
        curveType: 'Linear',
        img: 'linear_projection.png',
        imgPrimary: 'linear_projection_primary.png',
    },
    {
        projectionType: PROJECTION_TYPE.NORMAL,
        projectionVariable: 7,
        curveType: 'Wide',
        img: 'normal_wide_projection.png',
        imgPrimary: 'normal_wide_projection_primary.png',
    },
    {
        projectionType: PROJECTION_TYPE.NORMAL,
        projectionVariable: 5,
        curveType: 'Medium',
        img: 'normal_medium_projection.png',
        imgPrimary: 'normal_medium_projection_primary.png',
    },
    {
        projectionType: PROJECTION_TYPE.NORMAL,
        projectionVariable: 3,
        curveType: 'Narrow',
        img: 'normal_narrow_projection.png',
        imgPrimary: 'normal_narrow_projection_primary.png',
    },
    {
        projectionType: PROJECTION_TYPE.GAMMA,
        projectionVariable: -1,
        curveType: 'L Skew',
        img: 'gamma_leftskew_projection.png',
        imgPrimary: 'gamma_leftskew_projection_primary.png',
    },
    {
        projectionType: PROJECTION_TYPE.GAMMA,
        projectionVariable: 1,
        curveType: 'R Skew',
        img: 'gamma_rightskew_projection.png',
        imgPrimary: 'gamma_rightskew_projection_primary.png',
    },
];

export const BULK_OPTION = {
    START_DATE: 'start_date',
    END_DATE: 'end_date',
    CURVE: 'curve',
    AMOUNT: 'amount',
};

export const BULK_ASSIGN_OPTIONS = [
    { label: 'Start date', value: BULK_OPTION.START_DATE },
    { label: 'End date', value: BULK_OPTION.END_DATE },
    { label: 'Curve', value: BULK_OPTION.CURVE },
    { label: 'Amount', value: BULK_OPTION.AMOUNT },
];

export const DEFAULT_SUBCATEGORY_NAME = 'New subcategory';
export const DEFAULT_BUDGET_LINE_NAME = 'New budget line';
export const IN_PROGRESS_TEXT = 'In progress';

export const LOAN_RATE_TYPE = {
    FIXED: 'fixed',
    VARIABLE: 'variable',
};

export const PRO_FORMA_DEVELOPMENT_DEBT_RATE_OPTIONS = [
    { label: 'Fixed', value: LOAN_RATE_TYPE.FIXED },
    { label: 'Variable', value: LOAN_RATE_TYPE.VARIABLE },
];

// Used in test files for mock data
export const DEBT_PRIVATE_UUID = '12c7af91-3f6f-423a-946a-e1c4255873ef';
// Used in AddSourceModal to filter Bonds from source type dropdown
export const BONDS_SOURCE_TYPE_UUID = '4f42d654-c206-4c23-a247-33a66fe1ab85';
// Used in AddSourceModal to filter NOI (system-generated only) from source type dropdown
export const NOI_SOURCE_TYPE_UUID = '1ed1a8a5-9d6a-4b3e-9381-b79cbe9233c9';

export const DEAL_MIXED_USE_RESIDENTIAL_OVER_RETAIL = '99c9c7e1-aa7f-4fa2-8082-db3408cd58b2';

export const CALCULATION_OPTIONS: IOptionAnyValue[] = [
    { value: 'total_potential_income', label: 'Total potential income' },
    { value: 'gross_rent', label: 'Gross rent' },
    { value: 'total_rental_income', label: 'Total rental income' },
];

export const currencyColumnKeys = [
    'pricePerSf',
    'amountPerYear',
    'totalAmountPerYear',
    'pricePerUnit',
    'costPerUnitPerYear',
    'untrended',
    'trended',
    'sale',
    'amountBySF',
    'amount',
    'annualRent',
    'amountYear',
    'recoveryAmountPerYear',
    'pricePerSfPerYear',
    'valuePerSfPerYear',
    'recoveryValuePerSfPerYear',
];
export const alwaysShowCentsCurrencyColumnKeys = [
    'pricePerUnit',
    'costPerUnitPerYear',
    'amountByUnit',
    'pricePerSf',
    'amountBySF',
    'pricePerSfPerMonth',
    'pricePerUnitPerMonth',
    'pricePerSfPerYear',
    'valuePerSfPerYear',
    'amountPerYear',
    'recoveryValuePerSfPerYear',
    'valueByType',
];
export const stringColumnKeys = ['name', 'title', 'tenant', 'suite'];
export const percentColumnKeys = [
    'annualGrowth', 'percentOfTotalRent', 'percentOfIncome', 'percentOfEffectiveGrossRevenue',
    'rentGrowth', 'recoveryPercentage',
    // waterfall analysis columns
    'share', 'upTo', 'gpPromote', 'distToGP', 'distToLP', 'roi', 'irr',
    'allocationPercentage', 'recPercent', 'recoveryPercentage',
];

// Pro forma local storage keys corresponding to IProFormaLocalStorage
export const SELECTED_SCENARIO_KEY = 'selectedScenario';
export const SELECTED_PERIOD_CADENCE_KEY = 'selectedPeriodCadence';
export const USES_EXPANDED_CATEGORIES = 'usesExpandedCategories';
export const HIDE_WARNINGS_KEY = 'hideWarnings';
export const FORECAST_METRIC_KEY = 'forecastMetric';
export const WATTS_TYPE = 'wattsType';
export const POWER_CAPACITY_TYPE = 'powerCapacityType';

export const SHOW_CENTS_KEY = GlobalKeys.ShowCents;
export const SHOW_PARTNER_RETURNS_KEY = GlobalKeys.ShowPartnerReturns;

export enum WattsType {
    KILOWATTS = 'Kilo',
    MEGAWATTS = 'Mega',
}
export const KILOWATTS_IN_MEGAWATTS = 1000;

export const WATTS_OPTIONS = [
    { label: WattsType.KILOWATTS, value: WattsType.KILOWATTS },
    { label: WattsType.MEGAWATTS, value: WattsType.MEGAWATTS },
];

export const CADENCE_UI_TEXT: {[key: string]: string} = {
    [Periods.MONTHLY]: 'Month',
    [Periods.QUARTERLY]: 'Quarter',
    [Periods.ANNUALLY]: 'Year',
};

// Operating income handlers
export const OPERATIONS_INCOME_WS_HANDLERS = {
    UNIT_MIX: {
        CREATE: 'create_operating_income_unit_mix',
        UPDATE: 'update_operating_income_unit_mix',
        DELETE: 'delete_operating_income_unit_mix',
    },
    GROSS_RENT_ADJUSTMENT: {
        UPDATE: 'update_operating_gross_rent_adjustment',
    },
    OTHER_INCOME: {
        CREATE: 'create_operating_income_other_income_line_item',
        UPDATE: 'update_operating_income_other_income_line_item',
        DELETE: 'delete_operating_income_other_income_line_item',
    },
    INCOME_ADJUSTMENT: {
        CREATE: 'create_operating_income_adjustment',
        UPDATE: 'update_operating_income_adjustment',
        DELETE: 'delete_operating_income_adjustment',
    },
    TENANT: {
        CREATE: 'create_operating_income_tenant',
        UPDATE: 'update_operating_income_tenant',
        DELETE: 'delete_operating_income_tenant',
    },
    TENANT_DATA_CENTER: {
        CREATE: 'create_operating_income_tenant_data_center',
        UPDATE: 'update_operating_income_tenant_data_center',
        DELETE: 'delete_operating_income_tenant_data_center',
    },
    ESTIMATE: {
        CREATE: 'create_income_floor_estimate',
        UPDATE: 'update_income_floor_estimate',
        DELETE: 'delete_operating_income_floor_estimate',
    },
    ESTIMATE_DATA_CENTER: {
        CREATE: 'create_income_floor_estimate_data_center',
        UPDATE: 'update_income_floor_estimate_data_center',
        DELETE: 'delete_operating_income_floor_estimate_data_center',
    },
    RETAIL_OTHER_INCOME: {
        CREATE: 'create_operating_income_retail_other_income_line_item',
        UPDATE: 'update_operating_income_retail_other_income_line_item',
        DELETE: 'delete_operating_income_retail_other_income_line_item',
    },
    RETAIL_INCOME_ADJUSTMENT: {
        CREATE: 'create_operating_income_retail_adjustment',
        UPDATE: 'update_operating_income_retail_adjustment',
        DELETE: 'delete_operating_income_retail_adjustment',
    },
    BED_MIX: {
        CREATE: 'create_operating_income_bed_mix',
        UPDATE: 'update_operating_income_bed_mix',
        DELETE: 'delete_operating_income_bed_mix',
    },
};

export const OPERATIONS_EXPENSES_WS_HANDLERS = {
    OPEX: {
        CREATE: 'create_operating_expense_line_item',
        UPDATE: 'update_operating_expense_line_item',
        DELETE: 'delete_operating_expense_line_item',
    },
    CAPEX: {
        CREATE: 'create_operating_capital_expenditure',
        UPDATE: 'update_operating_capital_expenditure',
        DELETE: 'delete_operating_capital_expenditure',
    },
    LEASING_COST_RESERVES: {
        UPDATE: 'update_leasing_cost_reserves',
    },
};

export const DEFUALT_ROW_NAME_BY_HANDLER = {
    [OPERATIONS_INCOME_WS_HANDLERS.UNIT_MIX.CREATE]: 'New unit',
    [OPERATIONS_INCOME_WS_HANDLERS.BED_MIX.CREATE]: 'New bed type',
    [OPERATIONS_INCOME_WS_HANDLERS.OTHER_INCOME.CREATE]: 'New income',
    [OPERATIONS_INCOME_WS_HANDLERS.INCOME_ADJUSTMENT.CREATE]: 'New adjustment',
    [OPERATIONS_INCOME_WS_HANDLERS.RETAIL_OTHER_INCOME.CREATE]: 'New income',
    [OPERATIONS_INCOME_WS_HANDLERS.RETAIL_INCOME_ADJUSTMENT.CREATE]: 'New adjustment',
    [OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.CREATE]: 'New operating expense',
    [OPERATIONS_EXPENSES_WS_HANDLERS.CAPEX.CREATE]: 'New capital expenditure',
};

export const TOTAL_ANTICIPATED_COST = 'Total Anticipated Cost';
export const TOTAL_REVISED_BUDGET = 'Total Revised Budget';

export const FORECAST_METRIC_OPTIONS_NON_EARLY_PLANNING = [
    { label: TOTAL_ANTICIPATED_COST, value: TOTAL_ANTICIPATED_COST },
    { label: TOTAL_REVISED_BUDGET, value: TOTAL_REVISED_BUDGET },
];

export const CAPITAL_RESERVES_ROW = 'Capital reserves';
export const PROPERTY_TAXES_ROW = 'Property taxes';
export const STATIC_OPERATIONS_ROW_NAMES = new Set([CAPITAL_RESERVES_ROW]);

export enum VIEW_ENTITY {
    DEALS = 'deals',
    PROJECTS = 'projects',
}

export enum BudgetClassificationTypeEnum {
    DEFAULT = 'default',
    INTEREST_RESERVE = 'interest_reserve',
    FINANCING_FEE = 'financing_fee',
    DEVELOPER_FEE = 'developer_fee',
    OPERATING_RESERVE = 'operating_reserve',
}

export enum AdjustmentType {
    GENERAL_VACANCY = 'General Vacancy',
    CREDIT_LOSS = 'Credit Loss',
    OTHER = 'Other',
}

export const EDITABLE_BUDGET_CLASSIFICATION_TYPES = [BudgetClassificationTypeEnum.FINANCING_FEE, BudgetClassificationTypeEnum.DEVELOPER_FEE];

export const NON_DEFAULT_BUDGET_TYPES = [
    BudgetClassificationTypeEnum.DEVELOPER_FEE,
    BudgetClassificationTypeEnum.FINANCING_FEE,
    BudgetClassificationTypeEnum.INTEREST_RESERVE,
    BudgetClassificationTypeEnum.OPERATING_RESERVE,
];

export enum OperationExpenseType {
    STANDARD = 'standard',
    CAPITAL_RESERVES = 'capital_reserves',
    PROPERTY_TAXES = 'property_taxes',
    LEASING_COST_RESERVE = 'leasing_cost_reserve',
}

export enum ProFormaAreaMode {
    GROSS_BUILDABLE_AREA = 'GROSS_BUILD_AREA',
    FAR = 'FAR',
}

// All events are prefixed with "pro_forma:"
export const PRO_FORMA_WS_EVENT_NAMES = [
    'unitMix:created',
    'unitMix:updated',
    'unitMix:deleted',
    'bedMix:created',
    'bedMix:updated',
    'bedMix:deleted',
    'grossRentAdjustment:updated',
    'otherIncome:created',
    'otherIncome:updated',
    'otherIncome:deleted',
    'incomeAdjustments:created',
    'incomeAdjustments:updated',
    'incomeAdjustments:deleted',
    'retailOtherIncome:created',
    'retailOtherIncome:updated',
    'retailOtherIncome:deleted',
    'retailIncomeAdjustments:created',
    'retailIncomeAdjustments:updated',
    'retailIncomeAdjustments:deleted',
    'incomeTenants:created',
    'incomeTenants:updated',
    'incomeTenants:deleted',
    'incomeFloorEstimate:created',
    'incomeFloorEstimate:updated',
    'incomeFloorEstimate:deleted',
    'incomeFloorEstimateDataCenter:created',
    'incomeFloorEstimateDataCenter:updated',
    'incomeFloorEstimateDataCenter:deleted',
    'incomeTenantsDataCenter:created',
    'incomeTenantsDataCenter:updated',
    'incomeTenantsDataCenter:deleted',
    'opEx:created',
    'opEx:updated',
    'opEx:deleted',
    'capitalExpenditures:created',
    'capitalExpenditures:updated',
    'capitalExpenditures:deleted',
    'leasingCostReserves:updated',
    'propertyTax:updated',
    'updated',
    'pro_forma_source:created',
    'default_budget_classifications:created',
    'budget_classification:created',
    'recommended_budget_classifications:created',
    'start_development_with_boe_estimates:created',
    'budget_classifications:copied',
    'source:updated',
    'source:updated_metric_type',
    'operations:updated',
    'gross_rent_adjustment_parameters:updated',
    'budget_classification:updated',
    'budget_classification:deleted',
    'source:deleted',
    'gross_buildable_lot:created',
    'gross_buildable_lot:updated',
    'gross_buildable_lot:deleted',
    'disposition:updated',
    'pro_forma_waterfall_analysis:updated',
    'distribution_waterfall:basic:updated',
    'distribution_waterfall:multi_gp:updated',
    'pro_forma_partner:updated',
    'budget_classifications_based_on_project:created',
];

export const mockData: SensitivityTableValues = {
    values: [],
    verticalLabel: 'Hard + soft costs',
    horizontalLabel: 'Exit cap rate',
};

export enum ProjectActualsDiscrepanciesEnum {
    PRO_FORMA_START_DATE = 'pro_forma_start_date',
    BUILT_GROSS_SQUARE_FOOTAGE = 'built_gross_square_footage',
    OPERATION_START_DATE = 'operation_start_date',
    HOLD_PERIOD = 'hold_period',
}

export const tableValueClassName = (value: any) => {
    if (value === null || (typeof value === 'number' && value as number < 0)) return 'text-muted';
    return 'text-dark';
};

export enum RentAdjustmentNames {
    CONCESSIONS = 'Concessions',
    NON_REVENUE_UNITS = 'Non revenue units',
    GAIN_LOSS_TO_LEASE = 'Gain/loss to lease',
}

export const CASH_FLOW_TOOLTIP_MESSAGE = 'Cash flow data will populate once all necessary inputs have been entered.';

export const DEVELOPMENT_IN_PROGRESS_MSG = 'In progress - provide start/end dates for budget lines.';

export enum OperationsEntity {
    FLOOR_ALLOCATIONS = 'floor allocations',
    UNIT_MIX = 'unit mix',
    TENANTS = 'tenants'
}

export enum ProFormaSideNavSettingsMenuKeys {
    PERIOD_CADENCE = 'periodCadence',
    WATTS = 'wattsType',
    HIDE_WARNINGS = 'hideWarnings',
    SHOW_CENTS = 'showCents',
    SHOW_PARTNER_RETURNS = 'showPartnerReturns',
}

export enum ProFormaSideNavSettingsMenuLabels {
    PERIOD_CADENCE = 'Period cadence',
    WATTS = 'Watts',
    HIDE_WARNINGS = 'Hide warnings for Back-of-Envelope and pro forma discrepancies',
    SHOW_CENTS = 'Show cents',
    SHOW_PARTNER_RETURNS = 'Show partner returns',
}

export const OCCUPANCY_UPON_STABILIZATION = 'Determine the occupancy threshold at which the project is considered stabilized.';
export const LEASE_UP_PACE_TOOLTIP = 'Lease-up pace has been adjusted to ensure stabilized occupancy is reached within the hold period.';

export enum PowerCapacityNames {
    kW = 'kW',
    MW = 'MW',
}

export const PRO_FORMA_SIZE_POWER_CAPACITY_OPTIONS = [
    { value: PowerCapacityNames.kW, label: PowerCapacityNames.kW },
    { value: PowerCapacityNames.MW, label: PowerCapacityNames.MW },
];

export const APARTMENT_WITH_RETAIL_INCOME_TABS = {
    APARTMENT: { name: 'Overview', icon: faHouse },
    RETAIL: { name: 'Retail', icon: faShop },
};

export const APARTMENT_TAB_KEY = 'APARTMENT' satisfies keyof typeof APARTMENT_WITH_RETAIL_INCOME_TABS;
export const RETAIL_TAB_KEY = 'RETAIL' satisfies keyof typeof APARTMENT_WITH_RETAIL_INCOME_TABS;
